import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Fab, Grid, IconButton, Portal, Tooltip, Typography } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, PlaylistAdd as PlaylistAddIcon } from "@mui/icons-material";
import { useConfirm } from "material-ui-confirm";
import styled from "styled-components";
import * as L from "leaflet";
import { useTranslation } from "react-i18next";

import { ImportStatus, Poi } from "src/types";
import { usePoiDeleteMutation, usePois } from "src/hooks";
import { useCase } from "src/hooks/useCase";
import { Pane } from "src/components/Pane";
import { PaneListItemHoverActions, PaneListButton } from "src/components/PaneListButton";
import { PaneListEmptyPlaceholder } from "src/components/PaneListEmptyPlaceholder";
import { PoisOnMap } from "src/components/on-map/PoisOnMap";
import { IsEnabled } from "src/components/permissions";
import clsx from "clsx";
import { isDisabled } from "src/utils/permission.utils";

interface Props {
  map: L.Map;
  controlContainer: Element | null;
}

export function PoisPage(props: Props) {
  const { t } = useTranslation();
  const [refetchInterval, setRefetchInterval] = useState<false | number>(false);
  const { caseId = "", poiId } = useParams<{ caseId: string; poiId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();

  // async data
  const { data: acase } = useCase(caseId, { refetchInterval });
  const { data: pois, refetch: refetchPois } = usePois(caseId);

  // mutations
  const deleteMutation = usePoiDeleteMutation(caseId);

  /**
   * Effects
   */
  useEffect(updateRefreshInterval, [acase, refetchPois]);

  /**
   * Functions
   */
  function updateRefreshInterval() {
    if (acase?.poiImportStatus === ImportStatus.IMPORTING) {
      setRefetchInterval(1000);
    } else {
      refetchPois();
      setRefetchInterval(false);
    }
  }

  async function handleDelete(poi: Poi) {
    confirm({
      confirmationButtonProps: { autoFocus: true },
      title: t("label.areYouSure"),
      description: t("confirm.deletePoi", { name: poi.name }),
    }).then(() => {
      deleteMutation.mutate(poi.id);

      // if the deleted item is being viewed, return to list of items
      if (poiId === poi.id.toString()) {
        navigate(`/cases/${caseId}/pois`);
      }
    });
  }

  return render();

  function render() {
    if (!acase) return null;

    return (
      <>
        <StyledPane>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h2">{t("label.pointsOfInterest")}</Typography>
            </Grid>

            {renderPoisList()}

            <IsEnabled permission={acase.permissions.poi}>
              <Grid item xs={12}>
                <Button
                  startIcon={<PlaylistAddIcon />}
                  color="primary"
                  component={NavLink}
                  to={`/cases/${acase.id}/pois/import`}
                  fullWidth
                >
                  {t("label.bulkUpload")}
                </Button>
              </Grid>
            </IsEnabled>
          </Grid>
        </StyledPane>

        <PoisOnMap
          map={props.map}
          params={{ caseId, poiId }}
          callbacks={{
            onClick: (poiId) => {
              navigate(`/cases/${caseId}/pois/${poiId}`);
            },
          }}
        />
        {location.pathname.includes("pois") && (
          <Portal container={props.controlContainer}>
            {/* TODO: improve into seperate component when reused */}
            <Tooltip
              title={poiId !== "new" ? t<string>("label.addPointOfInterest") : t<string>("label.cancel")}
              placement="right"
            >
              <span>
                <StyledFab
                  color="default"
                  size="medium"
                  disabled={isDisabled(acase.permissions.poi)}
                  onClick={(event: any) => {
                    event.stopPropagation();
                    event.preventDefault();
                    if (poiId === "new") navigate(`/cases/${acase.id}/pois`);
                    else navigate(`/cases/${acase.id}/pois/new`);
                  }}
                  className={clsx({ "add-close-fab": poiId === "new" })}
                >
                  <AddIcon fontSize="small" />
                </StyledFab>
              </span>
            </Tooltip>
          </Portal>
        )}
      </>
    );
  }

  function renderPoisList() {
    if (!acase || !pois) return null;

    // if no pois, show empty placeholder
    if (pois.length === 0) {
      return (
        <Grid item xs={12}>
          <PaneListEmptyPlaceholder>{t("label.noPointOfInterestFound")}</PaneListEmptyPlaceholder>
        </Grid>
      );
    }

    return (
      <>
        {pois.map((poi) => (
          <Grid item xs={12} key={poi.id}>
            <PaneListButton to={`/cases/${acase.id}/pois/${poi.id}`} className="list-item">
              <div className="name">{poi.name}</div>
              <IsEnabled permission={acase.permissions.poi}>
                <PaneListItemHoverActions>
                  <IconButton
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      handleDelete(poi);
                    }}
                    size="large"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </PaneListItemHoverActions>
              </IsEnabled>
            </PaneListButton>
          </Grid>
        ))}
      </>
    );
  }
}

const StyledPane = styled(Pane)`
  .list-item {
    justify-content: space-between;
  }
`;

const StyledFab = styled(Fab)`
  transition: 300ms all;
  &.add-close-fab {
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: white;
    transform: rotate(45deg);
  }
`;
